import { useState } from 'react';
import { CalendarPlus, Plus, X } from 'lucide-react';
import type { JobVisit } from '../lib/api.js';
import {
  BLOCK_HOURS,
  durationLabel,
  formatStoredStart,
  shiftIsoDate,
  todayLocalIso,
  useTechNames,
  windowLabel,
  type Technician,
} from '../lib/dispatch-board.js';
import { useOrderVisits, useVisitMutations } from '../lib/visits.js';
import { Feedback } from './ui/feedback.js';

interface JobVisitsProps {
  spireId: number;
  /** Active roster — the techs a visit can be given to. */
  techs: ReadonlyArray<Technician>;
  /** The order's own (first) visit from its fop_* fields, shown for context and used as the add-form defaults. */
  first: { date: string; startHour: number; duration: number; techId: string } | null;
  /** Jump the board to a visit's day. Omitted where there is no board (the order page). */
  onGoToDate?: (date: string) => void;
}

const FIELD = 'rounded-md border bg-surface px-1.5 py-1 text-xs text-ink';
const BORDER = { borderColor: 'var(--color-border-tertiary)' } as const;
const DURATIONS = Array.from({ length: 24 }, (_, i) => (i + 1) * BLOCK_HOURS);

/**
 * The extra visits on a job — multi-day work and return trips — with add,
 * edit-in-place and remove. Every change saves immediately. The first visit
 * is the order's own schedule (edited on the board as before); this lists
 * each visit after it. Office roles only: mount it behind a role check.
 */
export function JobVisits({ spireId, techs, first, onGoToDate }: JobVisitsProps) {
  const query = useOrderVisits(spireId);
  const { create, update, remove } = useVisitMutations();
  const techName = useTechNames();
  const visits = query.data ?? [];

  // Add-form defaults: the day after the latest visit, same crew lead and
  // hours as the first visit — the common case is "same again tomorrow".
  const lastDate = visits.length > 0 ? visits[visits.length - 1]!.date : (first?.date ?? todayLocalIso());
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [until, setUntil] = useState('');
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState<number | null>(null);
  const [techId, setTechId] = useState('');
  const formDate = date || shiftIsoDate(lastDate, 1);
  const formStart = startTime || formatStoredStart(first?.startHour ?? 8);
  const formDuration = duration ?? first?.duration ?? 2;
  const formTech = techId || first?.techId || techs[0]?.id || '';
  const untilOk = !until || until >= formDate;
  const canAdd = !!formTech && untilOk && !create.isPending;

  const add = () => {
    create.mutate(
      {
        spireSalesOrderId: spireId,
        techId: formTech,
        date: formDate,
        startTime: formStart,
        durationHours: formDuration,
        ...(until && until !== formDate ? { untilDate: until } : {}),
      },
      {
        onSuccess: () => {
          setDate('');
          setUntil('');
          setOpen(false);
        },
      },
    );
  };

  const error = create.error ?? update.error ?? remove.error;

  return (
    <div className="space-y-1.5 text-xs">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-ink-subtle inline-flex items-center gap-1">
          <CalendarPlus className="h-3 w-3" aria-hidden />
          Visits
        </span>
        <span className="text-[11px] text-ink-subtle">
          {visits.length === 0 ? 'One visit' : `${visits.length + (first ? 1 : 0)} visits`}
        </span>
      </div>

      {(first || visits.length > 0) && (
        <ul className="space-y-1">
          {first && (
            <li className="flex items-center gap-2 flex-wrap text-ink-muted">
              <span className="w-10 shrink-0 text-[10px] uppercase tracking-wider text-ink-subtle">First</span>
              <span className="text-ink">{dayLabel(first.date)}</span>
              <span>{windowLabel(first.startHour, first.duration)}</span>
              <span>{techName(first.techId)}</span>
              {onGoToDate && <GoTo date={first.date} onGoToDate={onGoToDate} />}
            </li>
          )}
          {visits.map((v, i) => (
            <VisitRow
              key={v.id}
              index={i + (first ? 2 : 1)}
              visit={v}
              techs={techs}
              techName={techName}
              busy={update.isPending || remove.isPending}
              onPatch={(patch) => update.mutate({ id: v.id, patch })}
              onRemove={() => remove.mutate(v.id)}
              {...(onGoToDate ? { onGoToDate } : {})}
            />
          ))}
        </ul>
      )}

      {open ? (
        <div className="rounded-md border p-2 space-y-1.5" style={BORDER}>
          <div className="flex items-center gap-1.5 flex-wrap">
            <input type="date" aria-label="Visit date" value={formDate} onChange={(e) => setDate(e.target.value)} className={FIELD} style={BORDER} />
            <input type="time" step={1800} aria-label="Start time" value={formStart} onChange={(e) => setStartTime(e.target.value)} className={FIELD} style={BORDER} />
            <DurationSelect value={formDuration} onChange={setDuration} />
            <TechSelect value={formTech} techs={techs} techName={techName} onChange={setTechId} />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap text-ink-muted">
            <span>Repeat every day through</span>
            <input type="date" aria-label="Repeat through" value={until} min={formDate} onChange={(e) => setUntil(e.target.value)} className={FIELD} style={BORDER} />
            <span className="text-ink-subtle">(optional — skips their days off and holidays)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={add}
              disabled={!canAdd}
              className="inline-flex items-center gap-1 rounded-md bg-brand px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50"
            >
              {create.isPending ? 'Adding…' : until && until !== formDate ? 'Add visits' : 'Add visit'}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="rounded-md px-2 py-1 text-xs text-ink-muted hover:bg-surface-muted">
              Cancel
            </button>
            {!untilOk && <span className="text-amber-700">The repeat date is before the visit date.</span>}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={techs.length === 0}
          className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-ink hover:bg-surface-muted disabled:opacity-50"
          style={BORDER}
        >
          <Plus className="h-3 w-3" aria-hidden />
          Add another visit
        </button>
      )}

      {query.isError && <Feedback variant="error" size="sm">Couldn't load visits: {query.error.message}</Feedback>}
      {error && <Feedback variant="error" size="sm">{error.message}</Feedback>}
    </div>
  );
}

function VisitRow({
  index,
  visit,
  techs,
  techName,
  busy,
  onPatch,
  onRemove,
  onGoToDate,
}: {
  index: number;
  visit: JobVisit;
  techs: ReadonlyArray<Technician>;
  techName: (id: string) => string;
  busy: boolean;
  onPatch: (patch: { date?: string; startTime?: string; durationHours?: number; techId?: string; helperIds?: string[] }) => void;
  onRemove: () => void;
  onGoToDate?: (date: string) => void;
}) {
  const helperCandidates = techs.filter((t) => t.id !== visit.techId && !visit.helperIds.includes(t.id));
  return (
    <li className="flex items-center gap-1.5 flex-wrap">
      <span className="w-10 shrink-0 text-[10px] uppercase tracking-wider text-ink-subtle">#{index}</span>
      <CommitInput type="date" label={`Visit ${index} date`} value={visit.date} onCommit={(date) => onPatch({ date })} />
      <CommitInput type="time" label={`Visit ${index} start time`} value={visit.startTime} onCommit={(startTime) => onPatch({ startTime })} />
      <DurationSelect value={visit.durationHours} onChange={(h) => h !== visit.durationHours && onPatch({ durationHours: h })} />
      <TechSelect value={visit.techId} techs={techs} techName={techName} onChange={(id) => id !== visit.techId && onPatch({ techId: id })} />
      {visit.helperIds.map((id) => (
        <span key={id} className="inline-flex items-center gap-1 rounded-full border bg-surface px-2 py-0.5 text-[11px]" style={BORDER}>
          <span className="text-ink">{techName(id)}</span>
          <button
            type="button"
            onClick={() => onPatch({ helperIds: visit.helperIds.filter((h) => h !== id) })}
            aria-label={`Remove ${techName(id)} from visit ${index}`}
            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-ink-subtle hover:bg-surface-muted hover:text-red-700"
          >
            <X className="h-2.5 w-2.5" aria-hidden />
          </button>
        </span>
      ))}
      {helperCandidates.length > 0 && (
        <select
          value=""
          onChange={(e) => e.target.value && onPatch({ helperIds: [...visit.helperIds, e.target.value] })}
          aria-label={`Add a helper to visit ${index}`}
          className={FIELD}
          style={BORDER}
        >
          <option value="">+ helper</option>
          {helperCandidates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      )}
      {onGoToDate && <GoTo date={visit.date} onGoToDate={onGoToDate} />}
      <button
        type="button"
        onClick={onRemove}
        disabled={busy}
        className="ml-auto rounded-md px-1.5 py-1 text-xs text-ink-muted hover:bg-surface-muted hover:text-red-700 disabled:opacity-50"
        aria-label={`Remove visit ${index}`}
      >
        Remove
      </button>
    </li>
  );
}

/**
 * A date/time field that saves when the user leaves it (or presses Enter),
 * not on every keystroke — typing a year digit by digit fires `change` with
 * a string of valid-but-wrong dates.
 */
function CommitInput({
  type,
  label,
  value,
  onCommit,
}: {
  type: 'date' | 'time';
  label: string;
  value: string;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft !== null && draft !== '' && draft !== value) onCommit(draft);
    setDraft(null);
  };
  return (
    <input
      type={type}
      {...(type === 'time' ? { step: 1800 } : {})}
      aria-label={label}
      value={draft ?? value}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      className={FIELD}
      style={BORDER}
    />
  );
}

function GoTo({ date, onGoToDate }: { date: string; onGoToDate: (date: string) => void }) {
  return (
    <button type="button" onClick={() => onGoToDate(date)} className="text-[11px] text-ink-accent hover:underline">
      Show day
    </button>
  );
}

function DurationSelect({ value, onChange }: { value: number; onChange: (hours: number) => void }) {
  return (
    <select value={String(value)} onChange={(e) => onChange(Number(e.target.value))} aria-label="Length" className={FIELD} style={BORDER}>
      {/* Keep an off-grid stored value selectable rather than silently showing another. */}
      {!DURATIONS.includes(value) && <option value={String(value)}>{durationLabel(value)}</option>}
      {DURATIONS.map((h) => (
        <option key={h} value={String(h)}>
          {durationLabel(h)}
        </option>
      ))}
    </select>
  );
}

function TechSelect({
  value,
  techs,
  techName,
  onChange,
}: {
  value: string;
  techs: ReadonlyArray<Technician>;
  techName: (id: string) => string;
  onChange: (techId: string) => void;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Technician" className={FIELD} style={BORDER}>
      {/* A deactivated tech isn't in the roster; keep them visible as the current value. */}
      {value && !techs.some((t) => t.id === value) && <option value={value}>{techName(value)} (inactive)</option>}
      {techs.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </select>
  );
}

function dayLabel(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}
